// Tipos de componente de la estructura de salud, con el código de una letra que usa
// el back. Fuente única para los selectores que trabajan con estos códigos.
//
// Dirección ("H") queda afuera a propósito: es el establecimiento raíz, no un
// componente que se pueda asociar a un cargo. Servicio ya no es un tipo.
//
// OJO: el organigrama mantiene su propia copia de esta tabla en
// pages/programa-salud/estructuras/organigrama/utils/estructura.js (la constante TIPO). Son dos
// fuentes para lo mismo y conviene unificarlas en cuanto haya que tocar esa pantalla.
// Lo que YA esta unificado es `tiposHabilitadosPorComponenteMaximo`, mas abajo: vive aca
// y el organigrama la reexporta.

export const TIPOS_DE_COMPONENTE = [
    { cod: "D", label: "Departamento" },
    { cod: "I", label: "División" },
    { cod: "U", label: "Unidad" },
    { cod: "C", label: "CAPS" },
    { cod: "P", label: "Puesto" },
    { cod: "O", label: "Posta" },
    { cod: "R", label: "URESA" },
];

export const CODIGOS_DE_COMPONENTE = TIPOS_DE_COMPONENTE.map(({ cod }) => cod);

/**
 * Los tipos que un hospital admite, según su COMPONENTE MÁXIMO.
 *
 * Cada hospital tiene un nivel de complejidad, y ese nivel define el techo de la
 * estructura que se le puede colgar. `RNHEstablecimientoGet` lo devuelve ya resuelto en
 * `rnhEstablecimientoNivelComponenteMaximoCod` —ojo con el prefijo: el nivel viene
 * desnormalizado DENTRO de la fila del establecimiento, no hay un `rnhNivelComponenteMaximoCod`
 * suelto, y leerlo mal da `undefined` sin ningún error—.
 *
 * Todo lo que esté DESDE el techo hacia abajo se habilita:
 *
 *   "D" (departamento) → D, I, U, C, P, O, R
 *   "I" (división)     →    I, U, C, P, O, R
 *   "U" (unidad)       →       U, C, P, O, R
 *
 * El orden de `TIPOS_DE_COMPONENTE` ES la jerarquía: si alguna vez se reordena esa
 * tabla por otra razón —alfabética, por ejemplo— esta función deja de ser correcta
 * sin que nada falle.
 *
 * Falla ABIERTO: un valor vacío o no contemplado NO restringe, para no bloquear la
 * gestión por un dato inesperado. Eso tiene un costo que conviene saber: leer mal el
 * campo no da error, habilita todo, y el rechazo llega recién del back.
 *
 * El guard del -1 es obligatorio: sin él, un valor desconocido haría `slice(-1)` y
 * habilitaría SOLO el último tipo, que es lo contrario de fallar abierto.
 */
export const tiposHabilitadosPorComponenteMaximo = (componenteMaximo) => {
    const techo = CODIGOS_DE_COMPONENTE.indexOf(componenteMaximo);
    if (techo === -1) return CODIGOS_DE_COMPONENTE; // desconocido/ausente -> sin restriccion
    return CODIGOS_DE_COMPONENTE.slice(techo);
};

/**
 * El cargo que no se ata a ningun tipo de componente: sirve para cualquiera.
 *
 * Existe por el cargo "Sin Cargo": un agente sin cargo se puede asignar donde sea, asi
 * que su cargo no puede fijar un tipo. El codigo VACIO es a proposito — es lo que los
 * servicios convierten en el `null` que el back guarda en `rnhTipoComponenteCod`.
 */
export const TODOS_LOS_COMPONENTES = { cod: "", label: "Todos" };

/** Opciones del selector cuando "cualquier componente" es una eleccion valida. */
export const COMPONENTES_CON_TODOS = [TODOS_LOS_COMPONENTES, ...TIPOS_DE_COMPONENTE];

/** Los codigos de arriba, para el `oneOf` de yup. Incluye el vacio. */
export const CODIGOS_CON_TODOS = COMPONENTES_CON_TODOS.map(({ cod }) => cod);

/**
 * Códigos -> etiquetas legibles, separadas por coma. Para mostrar en grillas y vistas
 * de solo lectura: la fila guarda ["D","U"] y en pantalla se lee "Departamento, Unidad".
 * Descarta códigos que no estén en la tabla en vez de mostrarlos crudos.
 */
export const labelsDeComponentes = (cods) =>
    (cods ?? [])
        .map((cod) => TIPOS_DE_COMPONENTE.find((t) => t.cod === cod)?.label)
        .filter(Boolean)
        .join(", ");

/**
 * Código -> etiqueta legible. El back guarda y devuelve la sigla ("U"); en pantalla se
 * lee "Unidad". Si el código no está en la tabla devuelve el crudo, para no esconder un
 * valor inesperado.
 */
export const labelDeComponente = (cod) =>
    TIPOS_DE_COMPONENTE.find((t) => t.cod === cod)?.label ?? (cod ?? "");
